import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface PasivosData {
	deudasHipotecarias: string
	deudasBancarias: string
	otrasDeudas: string
	conyugeNombre: string
	conyugeDocumento: string
}

interface ManifestacionBienesPasivosScreenProps {
	onNext: (data: PasivosData) => void
	onBack: () => void
	data?: Partial<PasivosData>
	estadoCivil?: string
}

export function ManifestacionBienesPasivosScreen({
	onNext,
	onBack,
	data,
	estadoCivil,
}: ManifestacionBienesPasivosScreenProps) {
	const [formData, setFormData] = useState<PasivosData>({
		deudasHipotecarias: data?.deudasHipotecarias || '',
		deudasBancarias: data?.deudasBancarias || '',
		otrasDeudas: data?.otrasDeudas || '',
		conyugeNombre: data?.conyugeNombre || '',
		conyugeDocumento: data?.conyugeDocumento || '',
	})

	const showConyugeSection =
		estadoCivil === 'casado' || estadoCivil === 'union-convivencial'

	const handleSubmit = () => {
		onNext(formData)
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Pasivos y Cónyuge
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Detalla las deudas existentes. Todos los campos son opcionales.
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Deudas con Garantía Hipotecaria y Prendaria
							</label>
							<p className="text-xs text-neutral-500">
								Identificar el bien gravado, acreedor, monto y vencimiento.
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Hipoteca sobre inmueble en Av. Corrientes 1234. Acreedor: Banco Nación. Monto: $10.000.000. Vto: 12/2028"
								value={formData.deudasHipotecarias}
								onChange={(e) =>
									setFormData({
										...formData,
										deudasHipotecarias: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Deudas Bancarias
							</label>
							<p className="text-xs text-neutral-500">
								Detallar institución e importe.
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Préstamo personal Banco Galicia. Importe: $500.000"
								value={formData.deudasBancarias}
								onChange={(e) =>
									setFormData({ ...formData, deudasBancarias: e.target.value })
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Otras Deudas
							</label>
							<p className="text-xs text-neutral-500">
								Cualquier otra obligación financiera.
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Deuda con proveedor. Importe: $200.000"
								value={formData.otrasDeudas}
								onChange={(e) =>
									setFormData({ ...formData, otrasDeudas: e.target.value })
								}
							/>
						</div>

						{showConyugeSection && (
							<div className="mt-4 rounded-lg bg-blue-50 p-4">
								<h3 className="font-parkinsans mb-3 text-base font-medium text-blue-800">
									Datos del Cónyuge (Opcional)
								</h3>
								<p className="mb-3 text-xs text-neutral-600">
									Si desea incluir los bienes gananciales como garantía,
									complete los datos del cónyuge.
								</p>
								<div className="flex flex-col gap-3">
									<InputText
										label="Nombre y Apellido del Cónyuge"
										labelPosition="internal"
										size="l"
										placeholder="Nombre completo"
										value={formData.conyugeNombre}
										onChange={(e) =>
											setFormData({
												...formData,
												conyugeNombre: e.target.value,
											})
										}
									/>
									<InputText
										label="Documento del Cónyuge"
										labelPosition="internal"
										size="l"
										placeholder="DNI"
										value={formData.conyugeDocumento}
										onChange={(e) =>
											setFormData({
												...formData,
												conyugeDocumento: e.target.value,
											})
										}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
